import { useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useHookFormMask } from 'use-mask-input'
import { useLocalStorage } from '../../remote/local-storage'
import { Button } from '../components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { Input } from '../components/ui/input'
import { useToast } from '../hooks'
import { CardFormData, schemaCard } from '../validators/card-validator'

interface CardFormProps {
  data?: any
  idUsuario?: number
  type: 'ALUNO' | 'CLIENTE'
  closeDialog?: () => void
  updateList?: () => void
}
export default function CardForm({ data, type, idUsuario, closeDialog, updateList }: CardFormProps) {
  const { getUnitId } = useLocalStorage()
  const { httpClientPost } = useRemoteClientPost('finance')
  const { httpClientPut } = useRemoteClientPut('finance')
  const [params] = useSearchParams()
  const { showToast } = useToast()
  const isEditMode = Boolean(data?.id)

  const form = useForm<CardFormData>({
    resolver: zodResolver(schemaCard),
    defaultValues: {
      informacoesTitularCartao: {
        nome: data?.informacoesTitularCartao?.nome || '',
        email: data?.informacoesTitularCartao?.email || '',
        cpfCnpj: data?.informacoesTitularCartao?.cpfCnpj || '',
        numeroEndereco: data?.informacoesTitularCartao?.numeroEndereco || '',
        complementoEndereco: data?.informacoesTitularCartao?.complementoEndereco || '',
        cep: data?.informacoesTitularCartao?.cep || '',
        telefone: data?.informacoesTitularCartao?.telefone || ''
      },
      cartaoCredito: {
        nomeTitular: data?.cartaoCredito?.nomeTitular || '',
        numero: data?.cartaoCredito?.numero || '',
        ccv: data?.cartaoCredito?.ccv || '',
        anoExpiracao: data?.cartaoCredito?.anoExpiracao || '',
        mesExpiracao: data?.cartaoCredito?.mesExpiracao || ''
      },
      idUsuario: data?.idUsuario || idUsuario || 0,
      unidadeId: data?.unidadeId || Number(getUnitId())
    }
  })

  // const fetchUnits = async () => {
  //   try {
  //     const response = await remoteClientGet(
  //       Endpoints.units,
  //       formatParams({
  //         page: 0,
  //         size: 1000,
  //         sortParam: 'id',
  //         order: 'desc'
  //       })
  //     )
  //     const data = response?.data?.data?.content
  //     console.log(data)
  //     setDataUnits(
  //       data.map((unit: any) => ({
  //         ...unit,
  //         label: unit?.nomeFantasia,
  //         value: unit?.id
  //       }))
  //     )
  //     console.log(dataUnits)
  //   } catch (error) {
  //     console.error(error)
  //     throw error
  //   }
  // }

  useEffect(() => {
    form.setValue('unidadeId', Number(getUnitId()))
  }, [params])

  const onSubmit = async (formData: any) => {
    const formfiltered = {
      ...formData,
      informacoesTitularCartao: {
        ...formData.informacoesTitularCartao,
        cpfCnpj: formData.informacoesTitularCartao.cpfCnpj.replace(/\D/g, ''),
        telefone: formData.informacoesTitularCartao.telefone.replace(/\D/g, ''),
        cep: formData.informacoesTitularCartao.cep.replace(/\D/g, '')
      },
      cartaoCredito: {
        ...formData.cartaoCredito,
        numero: formData.cartaoCredito.numero.replace(/\D/g, '').trim(),
        ccv: formData.cartaoCredito.ccv.replace(/\D/g, '').trim(),
        anoExpiracao: formData.cartaoCredito.anoExpiracao.replace(/\D/g, '').trim(),
        mesExpiracao: formData.cartaoCredito.mesExpiracao.replace(/\D/g, '').trim()
      },
      idUsuario: formData.idUsuario,
      unidadeId: formData.unidadeId,
      tipo: type
    }

    try {
      if (isEditMode) {
        await httpClientPut(Endpoints.card, { ...formfiltered })
        closeDialog?.()
        updateList?.()
      } else {
        delete formData.id
        await httpClientPost(Endpoints.card + '/tokenizar', { ...formfiltered })
        closeDialog?.()
        updateList?.()
      }

      showToast('Requisição enviada com sucesso!', 'success')
      form.reset()
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  const registerWithMask = useHookFormMask(form.register)
  const formValues = form.watch()

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors }
  } = form

  useEffect(() => {
    console.log('Erros do formulário:', errors)
  }, [errors])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-128 p-4 w-full flex flex-col m-auto">
        <h3 className="text-3xl text-[#262626]">Formulário de Cartão</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="informacoesTitularCartao.nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Nome
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nome completo" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="informacoesTitularCartao.cpfCnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> CPF/CNPJ
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('informacoesTitularCartao.cpfCnpj', ['999.999.999-99', '99.999.999/9999-99'])} placeholder="Informe o CPF ou CNPJ" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.informacoesTitularCartao?.cpfCnpj?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="informacoesTitularCartao.email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Email
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Email" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.informacoesTitularCartao?.email?.message}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="informacoesTitularCartao.telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Telefone
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('informacoesTitularCartao.telefone', '(99) 99999-9999')} placeholder="(00) 00000-0000" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.informacoesTitularCartao?.telefone?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name="informacoesTitularCartao.cep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> CEP
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('informacoesTitularCartao.cep', '99999-999')} placeholder="00000-000" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.informacoesTitularCartao?.cep?.message}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="informacoesTitularCartao.numeroEndereco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Número
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Número" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.informacoesTitularCartao?.numeroEndereco?.message}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="informacoesTitularCartao.complementoEndereco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complemento</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Complemento" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.informacoesTitularCartao?.complementoEndereco?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="cartaoCredito.nomeTitular"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Nome no Cartão
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nome como aparece no cartão" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cartaoCredito.numero"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Número do Cartão
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('cartaoCredito.numero', '9999 9999 9999 9999')} placeholder="0000 0000 0000 0000" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.cartaoCredito?.numero?.message}</FormMessage>
                </FormItem>
              )}
            />
            {/* 
         
            <FormField
              control={form.control}
              name="unidadeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span>
                    Unidade
                  </FormLabel>
                  <FormControl>
                    <ComboSelectWithLabel
                      defaultValue={field.value}
                      data={dataUnits}
                      onChange={(e) => {
                        //console.log(e)
                        const id = e?.value
                        form.setValue('unidadeId', id)
                        form.trigger(['unidadeId'])
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors?.unidadeId?.message}</FormMessage>
                </FormItem>
              )}
            />
         */}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name="cartaoCredito.mesExpiracao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Mês
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('cartaoCredito.mesExpiracao', '99')} placeholder="MM" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.cartaoCredito?.mesExpiracao?.message}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cartaoCredito.anoExpiracao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Ano
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('cartaoCredito.anoExpiracao', '9999')} placeholder="AAAA" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.cartaoCredito?.anoExpiracao?.message}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cartaoCredito.ccv"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> CCV
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...registerWithMask('cartaoCredito.ccv', '999')} placeholder="000" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.cartaoCredito?.ccv?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>
        </div>

        <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-black mt-7">
          Enviar
        </Button>
      </form>
    </Form>
  )
}
